import React, { useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Module, Set } from '../types';

interface DraggableModuleProps {
    module: Module;
    selectedSets: { moduleId: string; setId: string }[];
    onToggleSetSelection: (moduleId: string, setId: string) => void;
    onToggleModuleSelection: (module: Module, checked: boolean) => void;
}

const DraggableModule: React.FC<DraggableModuleProps> = ({ module, selectedSets, onToggleSetSelection, onToggleModuleSelection }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `module-${module.id}`,
        data: { type: 'module', module }
    });

    // Check if all sets of this module are selected
    const allSetsSelected = module.sets.length > 0 && module.sets.every(set =>
        selectedSets.some(s => s.moduleId === module.id && s.setId === set.id)
    );
    const someSetsSelected = module.sets.some(set =>
        selectedSets.some(s => s.moduleId === module.id && s.setId === set.id)
    );

    return (
        <div className="module-item">
            <div
                ref={setNodeRef}
                {...listeners}
                {...attributes}
                className="module-header"
                style={{
                    opacity: isDragging ? 0.5 : 1,
                    cursor: 'grab',
                    userSelect: 'none'
                }}
            >
                <span className="drag-handle">⋮⋮</span>
                <input
                    type="checkbox"
                    checked={allSetsSelected}
                    ref={(el) => {
                        if (el) el.indeterminate = someSetsSelected && !allSetsSelected;
                    }}
                    onChange={(e) => {
                        e.stopPropagation();
                        onToggleModuleSelection(module, e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    style={{ marginRight: '8px', cursor: 'pointer', width: '16px', height: '16px' }}
                    title="Select all sets in this module"
                />
                <div className="module-info">
                    <div className="module-name">{`${module.name} (${module.worksheet_from} - ${module.worksheet_to})`}</div>
                    <div className="module-description">{module.description}</div>
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                        {module.sets.length} sets available
                    </div>
                </div>
                <button
                    className={`expand-button ${isExpanded ? 'expanded' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsExpanded(!isExpanded);
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                >
                    <i className="fas fa-chevron-circle-right"></i>
                </button>
            </div>

            {isExpanded && module.sets.length > 0 && (
                <div className="sets-container">
                    {module.sets.map((set) => (
                        <DraggableSet
                            key={set.id}
                            set={set}
                            module={module}
                            isSelected={selectedSets.some(s => s.moduleId === module.id && s.setId === set.id)}
                            onToggleSelection={onToggleSetSelection}
                        />
                    ))}
                </div>
            )}

            {isExpanded && module.sets.length === 0 && (
                <div className="sets-container">
                    <div style={{
                        padding: '10px',
                        textAlign: 'center',
                        color: '#999',
                        fontSize: '13px'
                    }}>
                        All sets from this module are in your pathway
                    </div>
                </div>
            )}
        </div>
    );
};

interface DraggableSetProps {
    set: Set;
    module: Module;
    isSelected: boolean;
    onToggleSelection: (moduleId: string, setId: string) => void;
}

const DraggableSet: React.FC<DraggableSetProps> = ({ set, module, isSelected, onToggleSelection }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `set-${set.id}`,
        data: { type: 'set', set, module }
    });

    return (
        <div
            ref={setNodeRef}
            className="set-item"
            style={{
                opacity: isDragging ? 0.5 : 1,
                userSelect: 'none',
                backgroundColor: isSelected ? '#eef2ff' : undefined
            }}
        >
            <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => {
                    e.stopPropagation();
                    onToggleSelection(module.id, set.id);
                }}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                style={{ marginRight: '8px', cursor: 'pointer', width: '16px', height: '16px' }}
            />
            <span 
                className="drag-handle" 
                {...listeners} 
                {...attributes}
                style={{ cursor: 'grab' }}
            >⋮</span>
            <span className="set-name">{set.name}</span>
            <span style={{ fontSize: '12px', color: '#999' }}>{set.description}</span>
        </div>
    );
};

interface ModuleSetTreeProps {
    modules: Module[];
    onAddModule: (module: Module) => void;
    onAddSet: (module: Module, set: Set) => void;
    selectedSets: { moduleId: string; setId: string }[];
    onToggleSetSelection: (moduleId: string, setId: string) => void;
    onToggleModuleSelection: (module: Module, checked: boolean) => void;
}

const ModuleSetTree: React.FC<ModuleSetTreeProps> = ({ modules, onAddModule, onAddSet, selectedSets, onToggleSetSelection, onToggleModuleSelection }) => {
    if (modules.length === 0) {
        return (
            <div className="empty-state">
                <div className="empty-state-icon">📚</div>
                <h3>No Modules Available</h3>
                <p>All modules are in your pathway or no modules match your search</p>
            </div>
        );
    }

    return (
        <div className="module-list-container">
            <div style={{ overflowY: 'auto', maxHeight: 'calc(110vh - 350px)' }}>
                {modules.map((module) => (
                    <DraggableModule
                        key={module.id}
                        module={module}
                        selectedSets={selectedSets}
                        onToggleSetSelection={onToggleSetSelection}
                        onToggleModuleSelection={onToggleModuleSelection}
                    />
                ))}
            </div>
        </div>
    );
};

export default ModuleSetTree;